import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getPoliciesOptions } from "js/formatting";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { getCorrespondenceList } from "redux/slices/getCorrespondenceListSlice";
import { useBreadcrumb } from "hooks/useBreadcrumb";
import { useRowPagination } from "hooks/useRowPagination";

import moment from "moment";
import config from "config/config";
import MessageRow from "components/MessageCenter/MessageRow";
import CategoryFilter from "components/MessageCenter/CategoryFilter";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import RELEASING_AutoCompleteComboBox from "components/General/AutoCompleteComboBox/RELEASING_AutoCompleteComboBox";
import CustomerRowPagination from "components/General/CustomerRowPagination";

export default function Messages() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((s) => s.user);
  const { policies } = useSelector((s) => s.data);

  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filteredAlerts, setFilteredAlerts] = useState([]);

  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState({
    displayAs: t("AutoComplate-display-as"),
    selectedPolicyDisplayAs: t("AutoComplate-display-as"),
    value: "all",
  });

  const [categories, setCategories] = useState([{ typeOfDocument: "ALL" }]);
  const [currentCategory, setCurrentCategory] = useState("ALL");
  const [searchParams, setSearchParams] = useSearchParams();
  const contractIdFromUrl = searchParams.get("contractId");
  const selectedCategory = searchParams.get("category");

  const { rowsForDisplay: current, rowPaginationProps: paginationProps } =
    useRowPagination(filteredAlerts, [5, 10, 25, "all"]);

  useEffect(() => {
    if (contractIdFromUrl) {
      const policy = policies.find((p) => p.oldPolicy === contractIdFromUrl);
      if (policy) {
        const policySelectionArray = getPoliciesOptions(policies);
        let selected = policySelectionArray.find(
          (p) => p.value === policy.oldPolicy
        );
        setSelectedOption({
          displayAs: selected?.displayAs,
          selectedPolicyDisplayAs: `${selected?.prodType}: ${policy.oldPolicy}`,
          value: selected?.value,
          prodType: selected?.prodType,
          insuredName: selected?.insuredName,
        });
      }
    }
  }, [contractIdFromUrl]);

  useEffect(() => {
    if (selectedCategory) {
      setCurrentCategory(selectedCategory);
    } else {
      setCurrentCategory("ALL");
    }
  }, [selectedCategory]);

  // check user first
  useEffect(() => {
    if (user.role.roleName === "Beneficiary") {
      navigate(`/access-denied`, { replace: true });
    }
  }, [user]);

  const getOldPolicy = (contrctId) => {
    if (policies?.length > 0) {
      let policy = policies.find((p) => p.contractID === contrctId);
      if (policy) {
        return policy.oldPolicy;
      }
    }
    return "";
  };

  const getFilteredAlerts = (categoryType) => {
    const policySpecificAlerts =
      selectedOption?.value === "" || selectedOption?.value === "all"
        ? alerts
        : alerts.filter((alert) => alert.oldPolicy === selectedOption.value);

    setFilteredAlerts(
      categoryType === "ALL"
        ? policySpecificAlerts
        : policySpecificAlerts.filter(
            (alert) =>
              alert.document.typeOfDocument?.toLowerCase() ===
              categoryType?.toLowerCase()
          )
    );
  };

  useEffect(() => {
    let filterAlertArray = alerts;
    if (selectedOption?.value !== "" && selectedOption?.value !== "all")
      filterAlertArray = filterAlertArray.filter(
        (alert) =>
          alert?.oldPolicy === selectedOption?.value ||
          alert?.type === "Company Alert"
      );
    if (currentCategory !== "ALL")
      filterAlertArray = filterAlertArray.filter(
        (alert) =>
          alert?.document?.typeOfDocument?.toLowerCase() ===
          currentCategory?.toLowerCase()
      );

    // set up dynamic filter categories
    setCategories([
      { typeOfDocument: "ALL" },
      ...Array.from(
        new Set(
          !selectedOption?.value || selectedOption?.value === "all"
            ? alerts.map((a) => a.document.typeOfDocument)
            : alerts
                .filter(
                  (alert) =>
                    alert?.oldPolicy === selectedOption?.value ||
                    alert?.type === "Company Alert"
                )
                .map((a) => a.document.typeOfDocument)
        )
      ).map((typeOfDocument) => ({ typeOfDocument })),
    ]);

    setFilteredAlerts(filterAlertArray);
  }, [selectedOption, currentCategory, alerts]);

  // get correspondense, setup master alerts, setup categories and filter alerts by category
  useEffect(() => {
    dispatch(
      getCorrespondenceList({
        agentId: "",
        policyId: policies.map((p) => p.contractID),
        contractIds: policies.map((p) => p.contractID),
        beginDate: moment()
          .subtract(parseInt(config.ALERT_AND_DOCUMENTS_DURATION), "months")
          .format("MM/DD/YYYY"),
        endDate: moment().format("MM/DD/YYYY"),
      })
    ).then((response) => {
      setIsLoading(false);
      const RESTRICTED_CORRESPONDENCE_TYPE =
        config.RESTRICTED_CORRESPONDENCE_TYPE.split(",");
      let alertArray = [];
      let allCategories = [];
      if (response?.meta?.requestStatus === "fulfilled") {
        response.payload.allAlerts.map((a) => {
          if (
            !RESTRICTED_CORRESPONDENCE_TYPE.includes(
              a.document.typeOfDocument?.toUpperCase()
            )
          ) {
            if (a?.type !== "alert") {
              alertArray.push({
                oldPolicy: getOldPolicy(a?.policy?.contractID),
                ...a,
                document: {
                  ...a.document,
                  typeOfDocument: a?.document?.PortalDisplayName,
                },
              });
            } else {
              alertArray.push({
                ...a,
                document: {
                  ...a.document,
                  typeOfDocument: a?.document?.PortalDisplayName,
                },
              });
            }
          }
        });
        response.payload.allCategories.forEach((a) => {
          if (
            !RESTRICTED_CORRESPONDENCE_TYPE.includes(
              a.typeOfDocument?.toUpperCase()
            )
          ) {
            allCategories.push({
              ...a,
              typeOfDocument: a?.typeOfDocument?.PortalDisplayName,
            });
          }
        });
        setAlerts(alertArray);
        setFilteredAlerts(alertArray);
        setCategories((currentCategories) => [
          ...currentCategories,
          ...allCategories,
        ]);
        const policySelectionArray = getPoliciesOptions(policies);
        let policiesOptions = [];

        policySelectionArray.map((policy) => {
          policiesOptions.push({
            ...policy,
            selectedPolicyDisplayAs: `${policy?.prodType}: ${policy.value}`,
          });
        });

        setOptions([selectedOption, ...policiesOptions]);
        if (policiesOptions?.length === 1)
          setSelectedOption(policiesOptions[0]);
      } else {
        if (
          response.payload[0] ===
          t("AlertsCorrespondence-correspondence-error-message-1")
        ) {
          setAlerts([]);
          setFilteredAlerts([]);
          setCategories((currentCategories) => [...currentCategories]);
        }
      }
    });
  }, [policies]);

  useBreadcrumb([
    { name: t("AlertsCorrespondence-home-name"), url: "/policies" },
    { name: t("AlertsCorrespondence-name"), url: "#" },
  ]);
  return (
    <>
      <div className="container">
        <h1>{t("AlertsCorrespondence-title")}</h1>
        <p>{t("AlertsCorrespondence-subTitle-2")}</p>
        <hr />
      </div>

      <EverlakeLoadingOverlay
        isLoading={isLoading}
        loadingText={t("AlertsCorrespondence-alertsTab-loading-text-2")}
      >
        <div className="container ui-component-spacing h-full min-h-[120px]">
          <div className="flex flex-col md:flex-row items-start">
            <div className="flex flex-col w-full md:block md:w-64 mb-4 md:flex-shrink-0">
              <RELEASING_AutoCompleteComboBox
                labelName={t("CustomerAlertsCorrespondence-contract-number")}
                inputPlaceholderText={t("AutoComplate-select-your-contract")}
                options={options}
                selectedOption={selectedOption}
                setSelectedOption={setSelectedOption}
                readOnly={policies?.length === 1}
                showBadge={true}
              />
            </div>
            <div className="w-full md:ml-4 xl:ml-6 mt-5 text-xs text-center font-semibold">
              {t("AlertsCorrespondence-disclaimer")}
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-start">
            <div className="flex flex-col w-full md:block md:w-64 md:flex-shrink-0">
              {/* filtering policies || category */}

              <div className="ui-card mb-8 text-center">
                {categories.map((category, categoryIndex) => {
                  return (
                    <CategoryFilter
                      key={`message-category-${category.typeOfDocument}-${categoryIndex}`}
                      category={category}
                      first={categoryIndex === 0}
                      last={categories.length - 1 === categoryIndex}
                      currentCategory={currentCategory}
                      setCurrentCategory={setCurrentCategory}
                      getFilteredAlerts={getFilteredAlerts}
                    />
                  );
                })}
              </div>
            </div>

            {/* message rows */}
            <div className="w-full md:ml-4 xl:ml-6">
              {current.length > 0 ? (
                <>
                  {current.map((message, idx) => (
                    <MessageRow
                      key={`message - ${message.document.name} - ${idx}`}
                      hasDescription={
                        message.type === "alert" ||
                        message.type === "Company Alert"
                      }
                      message={message}
                      policy={policies.find(
                        (p) => p.oldPolicy === message.oldPolicy
                      )}
                    />
                  ))}

                  <CustomerRowPagination {...paginationProps} />
                </>
              ) : (
                <div className="w-full md:ml-6 text-center">
                  <h3>{t("AlertsCorrespondence-policyAlerts-text-2")}</h3>
                </div>
              )}
            </div>
          </div>
        </div>
      </EverlakeLoadingOverlay>
    </>
  );
}
